import useAxios from 'hooks/useAxios';
import { BrandType, QueryBrandRes } from 'models/brand.model';
import { ExplorePostType } from 'models/explore-posts.model';
import { MealType, QueryMealRes } from 'models/meal.model';
import { CreateExplorePostType } from 'models/posts.model';
import React, { createContext, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { ROUTE_KEYS } from 'utils/constants';
import { convertTimeString, padArray } from 'utils/functions';

export type ExplorePostContextType = {
  formState: CreateExplorePostType;
  setFormState: (value: CreateExplorePostType) => void;
  isFormValid: boolean;
  setIsFormValid: (value: boolean) => void;
  brandData: BrandType[];
  mealData: MealType[];
  handleFormState: (exploreData: ExplorePostType) => void;
  contentUrl: string[];
  setContentUrl: (val: string[]) => void;
};

export const initialExplorePostData: CreateExplorePostType = {
  caption: '',
  cokitchenLocation: [],
  brand: [],
  meal: [],
  startDate: undefined,
  endDate: undefined,
  startTime: null,
  endTime: null,
  hasEndTime: false,
  promoPost: true,
  display_mode: '',
  cta: ''
};

export const ExploreContext = createContext({} as ExplorePostContextType);

export const ExploreContextProvider = () => {
  const location = useLocation();
  const [formState, setFormState] = useState<CreateExplorePostType>(initialExplorePostData);
  const [contentUrl, setContentUrl] = useState<string[]>([]);
  const [isFormValid, setIsFormValid] = useState(false);
  const { fetchData: fetchBrands, response: brandDataRes } = useAxios<QueryBrandRes>({
    url: `brands/fetch`,
    method: 'post'
  });

  const { fetchData: fetchMeals, response: mealDataRes } = useAxios<QueryMealRes>({
    url: `marketing/meals-multiple-brand_id`,
    method: 'post'
  });

  const brandData = useMemo(() => brandDataRes?.data ?? [], [brandDataRes]);
  const mealData = useMemo(() => mealDataRes?.data ?? [], [mealDataRes]);

  useEffect(() => {
    if (formState?.brand?.length > 0) {
      fetchMeals({}, { brand_ids: formState.brand });
    }
  }, [location, formState?.brand]);

  useEffect(() => {
    if (
      (location.pathname.includes(ROUTE_KEYS.PHOTO_CONTENT) ||
        location.pathname.includes(ROUTE_KEYS.VIDEO_CONTENT)) &&
      formState?.cokitchenLocation.length
    ) {
      fetchBrands({
        data: {
          cokitchen_ids: formState?.cokitchenLocation
        }
      });
    }
  }, [location, formState.cokitchenLocation]);

  const handleFormState = (exploreData: ExplorePostType) => {
    setFormState({
      brand: exploreData?.brands?.map((brand) => brand?.id) || [],
      caption: exploreData?.caption,
      endDate: exploreData?.end_date ? new Date(exploreData?.end_date) : undefined,
      promoPost: exploreData.is_promo_post || false,
      endTime: exploreData?.end_time ? convertTimeString(exploreData?.end_time) : null,
      startDate: exploreData?.start_date ? new Date(exploreData?.start_date) : undefined,
      startTime: exploreData?.start_time ? convertTimeString(exploreData?.start_time) : null,
      hasEndTime: !!exploreData?.end_date || !!exploreData?.end_time,
      meal: exploreData?.meals?.map((meal) => meal?.id) || [],
      cokitchenLocation: [exploreData?.cokitchen_id],
      type: exploreData?.type,
      display_mode: exploreData?.display_mode || '',
      cta: exploreData?.cta || ''
    });
    const contentUrl =
      exploreData?.type === 'photos'
        ? padArray(exploreData?.photos, 6)
        : [exploreData?.video?.videoUrl];
    setContentUrl(contentUrl);
  };

  const validateForm = (): boolean => {
    if (
      !formState.caption?.trim() ||
      !contentUrl.length ||
      !formState.cokitchenLocation.length ||
      !formState.startDate ||
      (formState.hasEndTime && !formState.endDate)
    )
      return false;

    return true;
  };

  useEffect(() => {
    setIsFormValid(validateForm());
  }, [formState]);

  const value = {
    formState,
    setFormState,
    isFormValid,
    setIsFormValid,
    brandData,
    mealData,
    handleFormState,
    contentUrl,
    setContentUrl
  };
  return (
    <ExploreContext.Provider value={value}>
      <Outlet />
    </ExploreContext.Provider>
  );
};
